"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.deleteCouponResponseSchema = exports.updateCouponResponseSchema = exports.updateCouponBodySchema = exports.createCouponResponseSchema = exports.createCouponBodySchema = exports.getCouponResponseSchema = exports.listCouponsResponseSchema = exports.listCouponsQuerySchema = exports.deleteCouponParamsSchema = exports.updateCouponParamsSchema = exports.createCouponParamsSchema = exports.getCouponParamsSchema = exports.listCouponsParamsSchema = void 0;
const zod_1 = require("zod");
const wallet_schemas_1 = require("../customer/wallet.schemas");
const common_schemas_1 = require("../base/common.schemas");
const api_schemas_1 = require("../base/api.schemas");
const timestamp_schemas_1 = require("../base/timestamp.schemas");
// ============================================
// MERCHANT COUPONS API SCHEMAS
// ============================================
// V1: coupon_qr_codes table removed (migration 029) - QR codes not used in V1
// Common merchant params schema
const _MerchantParamsSchema = zod_1.z.object({
    merchantId: zod_1.z.coerce.number().int().positive(),
});
// Coupon params schema (merchantId + couponId)
const _CouponParamsSchema = _MerchantParamsSchema.extend({
    couponId: zod_1.z.coerce.number().int().positive(),
});
// Params schemas for each endpoint
exports.listCouponsParamsSchema = _MerchantParamsSchema;
exports.getCouponParamsSchema = _CouponParamsSchema;
exports.createCouponParamsSchema = _MerchantParamsSchema;
exports.updateCouponParamsSchema = _CouponParamsSchema;
exports.deleteCouponParamsSchema = _CouponParamsSchema;
// REMOVED: createCouponDistributionParamsSchema - distributions are now part of createCoupon
// GET /merchants/:merchantId/coupons - Query (FR148-149)
exports.listCouponsQuerySchema = zod_1.z.object({
    status: zod_1.z.enum(['active', 'expired']).optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(50),
    cursor: zod_1.z.string().optional(),
});
// Internal schema for coupon summary in list - subset of entity plus analytics
const _CouponSummarySchema = wallet_schemas_1.CouponEntitySchema.pick({
    coupon_id: true,
    title: true,
    description: true,
    coupon_type: true,
    coupon_value: true,
    minimum_amount: true,
    is_active: true,
    created_at: true,
    updated_at: true,
    expires_at: true,
}).extend({
    distributed_count: zod_1.z.number().int().min(0),
    viewed_count: zod_1.z.number().int().min(0),
    redeemed_count: zod_1.z.number().int().min(0),
});
// GET /merchants/:merchantId/coupons - Response
exports.listCouponsResponseSchema = (0, api_schemas_1.listResponseSchema)(_CouponSummarySchema);
// Internal: Analytics schema for coupon detail view
const _CouponAnalyticsSchema = zod_1.z.object({
    total_distributed: zod_1.z.number().int(),
    total_views: zod_1.z.number().int(),
    total_redeemed: zod_1.z.number().int(),
    total_expired: zod_1.z.number().int(),
    top_sharers: zod_1.z.array(zod_1.z.object({
        user_id: zod_1.z.string(),
        display_name: zod_1.z.string().nullable(),
        share_count: zod_1.z.number().int(),
    })),
});
// GET /merchants/:merchantId/coupons/:couponId - Response (FR150-151)
// Coupon entity with visual design fields, multi-language fields, and analytics
exports.getCouponResponseSchema = (0, api_schemas_1.dataResponseSchema)(wallet_schemas_1.CouponEntitySchema.pick({
    coupon_id: true,
    title: true,
    description: true,
    coupon_type: true,
    coupon_value: true,
    minimum_amount: true,
    commission_basis_points: true,
    is_active: true,
    max_redemptions: true,
    current_redemptions: true,
    starts_at: true,
    expires_at: true,
    // Lock indicator (FR35 - core fields locked after distribution)
    distributed_at: true,
    // Multi-language fields (FR140-141, FR183)
    title_fr: true,
    description_fr: true,
    title_zh: true,
    description_zh: true,
}).extend({
    // Visual design fields (FR178-180)
    background_image_url: zod_1.z.string().url().nullable(),
    // Analytics for modal (FR150-151)
    analytics: _CouponAnalyticsSchema,
}));
// POST /merchants/:merchantId/coupons - Body (FR17-25, FR178-182, FR140-141)
// CRITICAL: distributions is REQUIRED - you cannot create a coupon without distributing it
// After creation+distribution, coupon is LOCKED (distributed_at is set)
exports.createCouponBodySchema = zod_1.z
    .object({
    title: zod_1.z.string().min(2).max(200),
    description: zod_1.z.string().min(2).max(500),
    coupon_type: wallet_schemas_1.CouponTypeSchema,
    coupon_value: zod_1.z.number().int().positive().nullable(), // % or cents - nullable for free_item/bogo
    text_value: zod_1.z.string().max(500).nullable(), // For free_item/bogo descriptions
    minimum_amount: common_schemas_1.moneyAmountSchema.nullable(), // FR23
    commission_basis_points: common_schemas_1.basisPointsSchema, // FR184
    starts_at: timestamp_schemas_1.timestampIsoSchema.nullable(),
    expires_at: timestamp_schemas_1.timestampIsoSchema, // FR22 - required field, must be future date
    max_redemptions: zod_1.z.number().int().positive().nullable(),
    // Visual design (FR178-180)
    background_image_url: zod_1.z.string().url().nullable(),
    // Multi-language support (FR140-141, FR183)
    title_fr: zod_1.z.string().min(2).max(200).nullable(),
    description_fr: zod_1.z.string().min(2).max(500).nullable(),
    title_zh: zod_1.z.string().min(2).max(200).nullable(),
    description_zh: zod_1.z.string().min(2).max(500).nullable(),
    // REQUIRED: Distribution data - cannot create coupon without distributing
    // (FR26-31: Member selection and auto-deposit to wallets)
    distributions: zod_1.z
        .array(zod_1.z.object({
        user_id: common_schemas_1.uuidSchema,
        instance_count: zod_1.z.number().int().min(1).max(100),
    }))
        .min(1), // At least one distribution required
})
    .superRefine((data, ctx) => {
    // Type-specific validation
    if (data.coupon_type === 'percentage' || data.coupon_type === 'fixed_amount') {
        if (data.coupon_value === null || data.coupon_value <= 0) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'Discount value is required for this coupon type',
                path: ['coupon_value'],
            });
        }
        // Percentage-specific validation
        if (data.coupon_type === 'percentage' && data.coupon_value !== null && data.coupon_value > 100) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'Percentage discount cannot exceed 100%',
                path: ['coupon_value'],
            });
        }
    }
    if (data.coupon_type === 'free_item' || data.coupon_type === 'bogo') {
        if (!data.text_value || data.text_value.trim().length === 0) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'Offer description is required for this coupon type',
                path: ['text_value'],
            });
        }
    }
    // French translation validation: if title provided, description required (and vice versa)
    if ((data.title_fr && !data.description_fr) || (!data.title_fr && data.description_fr)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Both French title and description are required together',
            path: data.title_fr ? ['description_fr'] : ['title_fr'],
        });
    }
    // Chinese translation validation: if title provided, description required (and vice versa)
    if ((data.title_zh && !data.description_zh) || (!data.title_zh && data.description_zh)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Both Chinese title and description are required together',
            path: data.title_zh ? ['description_zh'] : ['title_zh'],
        });
    }
});
// POST /merchants/:merchantId/coupons - Response
// Includes distribution results since distributions is now required
exports.createCouponResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    coupon_id: zod_1.z.number().int(),
    title: zod_1.z.string(),
    coupon_type: wallet_schemas_1.CouponTypeSchema,
    is_active: zod_1.z.boolean(),
    created_at: timestamp_schemas_1.timestampIsoSchema,
    distributed_at: timestamp_schemas_1.timestampIsoSchema, // Always set now since distribution is required
    // Distribution results
    total_instances_created: zod_1.z.number().int(),
    member_count: zod_1.z.number().int(),
}));
// PATCH /merchants/:merchantId/coupons/:couponId - Body
exports.updateCouponBodySchema = zod_1.z
    .object({
    title: zod_1.z.string().min(2).max(200).optional(),
    description: zod_1.z.string().min(2).max(500).optional(),
    expires_at: timestamp_schemas_1.timestampIsoSchema.nullable().optional(),
    // Note: coupon_type, coupon_value, minimum_amount locked after distribution (FR35)
    background_image_url: zod_1.z.string().url().nullable().optional(),
    // Multi-language support (FR140-141, FR183)
    title_fr: zod_1.z.string().min(2).max(200).nullable().optional(),
    description_fr: zod_1.z.string().min(2).max(500).nullable().optional(),
    title_zh: zod_1.z.string().min(2).max(200).nullable().optional(),
    description_zh: zod_1.z.string().min(2).max(500).nullable().optional(),
})
    .superRefine((data, ctx) => {
    // French translation validation: if title provided, description required (and vice versa)
    // Only validate if at least one French field is being updated
    const hasFrTitle = data.title_fr !== undefined;
    const hasFrDesc = data.description_fr !== undefined;
    if (hasFrTitle !== hasFrDesc) {
        // Only one French field is being updated - that's fine for partial update
        // But if one is explicitly set to a value and other is null, that's an error
        if ((data.title_fr && data.description_fr === null) || (data.title_fr === null && data.description_fr)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'Both French title and description must be set or cleared together',
                path: data.title_fr ? ['description_fr'] : ['title_fr'],
            });
        }
    }
    // Chinese translation validation
    const hasZhTitle = data.title_zh !== undefined;
    const hasZhDesc = data.description_zh !== undefined;
    if (hasZhTitle !== hasZhDesc) {
        if ((data.title_zh && data.description_zh === null) || (data.title_zh === null && data.description_zh)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'Both Chinese title and description must be set or cleared together',
                path: data.title_zh ? ['description_zh'] : ['title_zh'],
            });
        }
    }
});
// PATCH /merchants/:merchantId/coupons/:couponId - Response
exports.updateCouponResponseSchema = (0, api_schemas_1.dataResponseSchema)(wallet_schemas_1.CouponEntitySchema.pick({
    coupon_id: true,
    title: true,
    updated_at: true,
}));
// DELETE /merchants/:merchantId/coupons/:couponId - Response
exports.deleteCouponResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    coupon_id: wallet_schemas_1.CouponEntitySchema.shape.coupon_id,
    deleted_at: timestamp_schemas_1.timestampIsoSchema,
}));
// REMOVED: createCouponDistributionBodySchema and createCouponDistributionResponseSchema
// Distribution is now merged into createCoupon - distributions field is REQUIRED in createCouponBodySchema
// The createCouponResponseSchema now includes distribution results (total_instances_created, member_count)
